import React, { useContext } from "react";
import { Text } from "components/common";
import { differenceInDays, format } from "date-fns";
import { UserContext } from "context";
import {
  BillingInfoItemStyle,
  BillingInfoLeft,
  BillingInfoRight,
  BillingInfoRootStyle,
  BillingInfoStyle,
  ManageSubscriptionButtonStyle,
  ManageSubscriptionStyle,
  TrialEndStyle,
} from "features/settings/styles";
import {
  HeartIcon,
  InfiniteSpinnerIcon,
  WarningCircleIcon,
} from "components/icons";
import { CreditProgress } from "./progress";
import { FontFamily, theme } from "assets/theme";

type Props = {
  balance: number;
  currentPlan: {
    //name: string;
    expireIn?: Date;
    isTrial?: boolean;
    isFree?: boolean;
    startAt?: Date;
    cancelAt?: Date;
    billingCycle?: string;
  };
  stripeCustomerId?: string;
  pricing: {
    normalPrice: number;
    discountPrice: number;
  };
  vipUser?: boolean;
};
export const CreditBalance: React.FC<Props> = ({
  balance,
  currentPlan,
  stripeCustomerId,
  pricing,
  vipUser,
}) => {
  const { state: userState } = useContext(UserContext);
  const [loading, setLoading] = React.useState(false);
  // get the date at the 1st of next month
  const nextMonth = new Date();
  nextMonth.setMonth(nextMonth.getMonth() + 1);
  nextMonth.setDate(1);
  nextMonth.setHours(0, 0, 0, 0);

  const isFree = currentPlan?.isFree;
  const isTrial = currentPlan?.isTrial;
  const endIn = differenceInDays(
    currentPlan?.expireIn || new Date(),
    new Date(),
  );
  const expirationDate = format(
    currentPlan?.expireIn || new Date(),
    "EEEE, MMMM do",
  );

  const currentPrice =
    currentPlan?.billingCycle === "yearly"
      ? pricing.discountPrice
      : pricing.normalPrice;

  return (
    <div className={ManageSubscriptionStyle}>
      {isTrial && !isFree && (
        <div className={TrialEndStyle}>
          <div>
            {vipUser ? (
              <div
                style={{
                  marginRight: "8px",
                  width: "20px",
                  height: "20px",
                  backgroundColor: theme.colors.text["highlight/1"],
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <HeartIcon fill="dark" />
              </div>
            ) : (
              <WarningCircleIcon
                fill="highlight/1"
                style={{ marginRight: "8px" }}
              />
            )}
          </div>
          {vipUser ? (
            <div>
              <Text.Body marginBottom={1} fontFamily={FontFamily.RoobertMedium}>
                Thanks for being an early supporter—your access is on us!
              </Text.Body>
              <Text.BodyXS color="seconday">
                You’ve been with us from the start, and that means a lot. As a
                thank-you, you’re staying on the Executive assistant plan—free
                for now. We’ll keep you posted on any future updates, but for
                now, enjoy the perks, and thanks for being part of the journey!
              </Text.BodyXS>
            </div>
          ) : (
            <div>
              <Text.Body marginBottom={1}>Trial ends in {endIn} days</Text.Body>
              <Text.BodyXS color="seconday">
                Your trial period will expire on {expirationDate}. You will
                automatically be downgraded to Free Plan, after which you can
                decide to purchase Premium or stay free.
              </Text.BodyXS>
            </div>
          )}
        </div>
      )}
      {isFree && !isTrial && (
        <div>
          <Text.BodyXS marginBottom={1}>Remaining monthly credits</Text.BodyXS>
          <Text.Body2XS color="seconday">
            With your free plan you get 300 credits monthly. They reset at the
            beginning of every month.
          </Text.Body2XS>

          <CreditProgress value={300 - balance} max={300} />
        </div>
      )}
      {!isTrial && !isFree && stripeCustomerId && (
        <div className={BillingInfoStyle}>
          <Text.BodyXS marginBottom={4}>Billing info</Text.BodyXS>
          <div className={BillingInfoRootStyle}>
            <div className={BillingInfoLeft}>
              <div className={BillingInfoItemStyle}>
                <Text.BodySM color="seconday" marginBottom={1.5}>
                  Plan
                </Text.BodySM>
                <Text.BodySM color="seconday" marginBottom={1.5}>
                  Payment
                </Text.BodySM>
                {currentPlan?.startAt && (
                  <Text.BodySM color="seconday" marginBottom={1.5}>
                    Subscription start
                  </Text.BodySM>
                )}
                {currentPlan?.expireIn && (
                  <Text.BodySM color="seconday">
                    {currentPlan?.cancelAt
                      ? "Subscription end"
                      : "Next payment"}
                  </Text.BodySM>
                )}
              </div>
              <div className={BillingInfoItemStyle}>
                <Text.BodySM marginBottom={1.5}>
                  ${currentPrice}/
                  {currentPlan?.billingCycle === "yearly" ? "yr" : "mo"}
                </Text.BodySM>
                <Text.BodySM
                  marginBottom={1.5}
                  style={{ textTransform: "capitalize" }}
                >
                  {currentPlan?.billingCycle || "-"}
                </Text.BodySM>
                {currentPlan?.startAt && (
                  <Text.BodySM marginBottom={1.5}>
                    {format(currentPlan?.startAt, "MMMM do, yyyy")}
                  </Text.BodySM>
                )}
                {currentPlan?.expireIn && (
                  <Text.BodySM>
                    {format(currentPlan.expireIn, "MMMM do, yyyy")}
                  </Text.BodySM>
                )}
              </div>
            </div>
            <form
              action={`${process.env.REACT_APP_API_URL}/billing/customer-portal`}
              method="POST"
              className={BillingInfoRight}
            >
              <input type="hidden" name="userId" value={userState?._id} />
              <input type="hidden" name="customerId" value={stripeCustomerId} />
              <button
                type="submit"
                onClick={() => setLoading(true)}
                className={ManageSubscriptionButtonStyle}
              >
                Manage Subscription
                {loading ? (
                  <InfiniteSpinnerIcon
                    fill="primary"
                    style={{ height: "16px", marginLeft: "8px" }}
                  />
                ) : null}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
